import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { formatDate } from '../utils/formatters';
import { Clock, Users, Calendar, FileText, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Leaves({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [leaves, setLeaves] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [formData, setFormData] = useState({
    leave_type: 'Sick Leave',
    from_date: '',
    to_date: '',
    number_of_days: 1,
    reason: ''
  });

  useEffect(() => {
    fetchLeaves();
    fetchDashboardStats();
    fetchLeaveBalances();
  }, []);

  const fetchLeaveBalances = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = userData.employee_id;
      
      if (employeeId) {
        const response = await axios.get(`${API}/leave-balance/employee/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeaveBalances(response.data.balances || []);
      }
    } catch (error) {
      console.error('Failed to fetch leave balances:', error);
    }
  };

  const fetchLeaves = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaves`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaves(response.data);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leaves`, {
        ...formData,
        start_date: formData.from_date,
        end_date: formData.to_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Leave request submitted successfully!', 'success');
      setShowForm(false);
      setFormData({
        leave_type: 'Sick Leave',
        from_date: '',
        to_date: '',
        number_of_days: 1,
        reason: ''
      });
      fetchLeaves();
    } catch (error) {
      console.error('Submit leave error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leaves/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Leave approved successfully!', 'success');
      fetchLeaves();
    } catch (error) {
      console.error('Approve leave error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleReject = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leaves/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Leave rejected successfully!', 'success');
      fetchLeaves();
    } catch (error) {
      console.error('Reject leave error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Leave Management</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 rounded-lg text-white font-medium"
            style={{backgroundColor: '#2b3380'}}
          >
            {showForm ? 'Cancel' : '+ Apply Leave'}
          </button>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#fff3e0'}}>
                <Clock className="w-6 h-6" style={{color: '#ff9800'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#ff9800'}}>{dashboardStats?.pending_leaves || 0}</p>
                <p className="text-sm text-gray-500">Pending Leaves</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <CheckCircle className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>{dashboardStats?.on_leave_today || 0}</p>
                <p className="text-sm text-gray-500">On Leave Today</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Users className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{dashboardStats?.total_employees || 0}</p>
                <p className="text-sm text-gray-500">Total Employees</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#eceff1'}}>
                <Calendar className="w-6 h-6" style={{color: '#607d8b'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#607d8b'}}>{leaveBalances.length > 0 ? leaveBalances[0].available_days.toFixed(1) : '0'}</p>
                <p className="text-sm text-gray-500">Leave Balance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leave Balances Card */}
        {leaveBalances.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Your Leave Balances ({new Date().getFullYear()})</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {leaveBalances.map((balance) => (
                <div key={balance.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#2b3380] transition">
                  <div className="text-sm text-gray-600 mb-1">{balance.leave_type_name}</div>
                  <div className="text-3xl font-bold text-[#2b3380] mb-2">{balance.available_days.toFixed(1)}</div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Allocated: {balance.allocated_days}</div>
                    <div>Used: {balance.used_days}</div>
                    <div>Pending: {balance.pending_days}</div>
                    {balance.carried_forward_days > 0 && (
                      <div className="text-purple-600">CF: {balance.carried_forward_days}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Apply for Leave</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Leave Type</label>
                  <select
                    name="leave_type"
                    value={formData.leave_type}
                    onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="Sick Leave">Sick Leave</option>
                    <option value="Casual Leave">Casual Leave</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Days</label>
                  <input
                    type="number"
                    name="number_of_days"
                    value={formData.number_of_days}
                    onChange={(e) => setFormData({...formData, number_of_days: parseFloat(e.target.value)})}
                    min="0.5"
                    step="0.5"
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Date</label>
                  <input
                    type="date"
                    name="from_date"
                    value={formData.from_date}
                    onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">To Date</label>
                  <input
                    type="date"
                    name="to_date"
                    value={formData.to_date}
                    onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Reason</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-white font-medium"
                style={{backgroundColor: '#2b3380'}}
              >
                Submit Request
              </button>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Leave Requests</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaves.map((leave) => (
                  <tr key={leave.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.employee_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.leave_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(leave.start_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(leave.end_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.number_of_days}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        leave.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        leave.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {leave.status}
                      </span>
                    </td>
                    {(user.role === 'Admin' || user.role === 'HR Manager') && leave.status === 'Pending' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleApprove(leave.id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(leave.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Leaves;
